import React, { useEffect, useState } from 'react';
import { Image, View } from 'react-native';
import { confirmAction, showError, showSuccess } from '../../toast';
import { api, API_URL, money } from '../../api';
import { useAuth } from '../../AuthContext';
import { districtsFor, OTHER_DISTRICT } from '../../constants';
import { findSettlement, Place } from '../../geo';
import { SettlementPicker } from '../../settlement-picker';
import { Button, Card, Input, Label, Muted, Screen, Select, Title } from '../../ui';
import { pickAndUploadPhotos } from '../../photo-picker';
import { getLocale, t } from '../../i18n';

export default function AddApartmentScreen({ navigation }: any) {
  const { user } = useAuth();
  const isAgency = user?.role === 'AGENCY';
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerLastName, setOwnerLastName] = useState('');
  const [managementPercent, setManagementPercent] = useState('');
  const [address, setAddress] = useState('');
  // Населено място: град/село + област + община (от официалния списък)
  const [place, setPlace] = useState<Place | null>(null);
  const city = place?.city ?? '';
  const [district, setDistrict] = useState('');
  const [customDistrict, setCustomDistrict] = useState('');
  const [area, setArea] = useState('');
  const [floor, setFloor] = useState('');
  const [rooms, setRooms] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [billingPreview, setBillingPreview] = useState<string | null>(null);

  // При платен абонамент новият имот се дотаксува веднага — казваме го ПРЕДИ добавянето
  useEffect(() => {
    api('/billing/subscription')
      .then((d) => {
        const sub = d.subscription;
        const per = d.pricing?.perApartmentCents;
        // При абонамент през App Store няма дотаксуване от карта — Apple
        // преизчислява сама при смяна на плана.
        if (
          sub?.status === 'ACTIVE' &&
          sub.provider !== 'APPLE' &&
          sub.currentPeriodEnd &&
          new Date(sub.currentPeriodEnd) > new Date() &&
          per &&
          !d.pricing?.custom
        ) {
          const end = new Date(sub.currentPeriodEnd);
          const fraction = Math.min(1, Math.max(0, (end.getTime() - Date.now()) / (30 * 24 * 60 * 60 * 1000)));
          setBillingPreview(
            t('Абонаментът ви е платен до {date}. С добавянето на имота автоматично ще се изтеглят около {amount} от картата ви — пропорционално за оставащите дни. След това имотът влиза в месечната сума.', {
              date: end.toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG'),
              amount: money(Math.round(per * fraction)),
            })
          );
        }
      })
      .catch(() => {});
  }, []);

  const pickPhoto = async () => {
    // избор на НЯКОЛКО снимки наведнъж — качват се паралелно
    const urls = await pickAndUploadPhotos();
    if (urls.length) setPhotos((p) => [...p, ...urls]);
  };

  // при „Друг (ръчно)“ важи написаното на ръка
  const pickedDistrict = district === OTHER_DISTRICT || !district ? customDistrict.trim() : district;

  const submit = async () => {
    setLoading(true);
    try {
      const created = await api('/apartments', {
        body: {
          address,
          city,
          ...(place ? { region: place.region, municipality: place.municipality } : {}),
          ...(pickedDistrict ? { district: pickedDistrict } : {}),
          ...(area ? { area: Number(area) } : {}),
          ...(floor ? { floor: Number(floor) } : {}),
          ...(rooms ? { rooms: Number(rooms) } : {}),
          ...(description ? { description } : {}),
          photos,
          ...(isAgency
            ? {
                ownerPhone,
                ownerFirstName: ownerFirstName || undefined,
                ownerLastName: ownerLastName || undefined,
                ...(managementPercent ? { managementPercent: Number(managementPercent.replace(',', '.')) } : {}),
              }
            : {}),
        },
      });
      if (created?.billing?.chargedCents) {
        showSuccess(t('Имотът е добавен. Изтеглени са {amount} за оставащите дни от периода.', { amount: money(created.billing.chargedCents) }));
      }
      navigation.goBack();
    } catch (e: any) {
      // Планът в App Store покрива по-малко имоти → предлагаме смяна на плана
      if (e?.code === 'APPLE_LIMIT') {
        confirmAction(t('Лимит на плана'), e.message, t('Смени плана'), () => navigation.navigate('Subscription'), {
          cancelLabel: t('По-късно'),
        });
      } else {
        showError(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{t('Нов имот')}</Title>
      <Card>
        <Label>{t('Адрес')}</Label>
        <Input value={address} onChangeText={setAddress} placeholder={t('ул. Витоша 15, ап. 7')} />
        <Label>{t('Населено място')}</Label>
        <SettlementPicker
          value={place}
          onChange={(p) => {
            setPlace(p);
            setDistrict(''); // кварталите зависят от града
            setCustomDistrict('');
          }}
        />
        {/* Квартали: списък за големите градове; свободен текст за останалите
            градове; селата нямат квартали. */}
        {city && districtsFor(city).length > 1 ? (
          <>
            <Label>{t('Квартал / район')}</Label>
            <Select
              placeholder={t('Изберете квартал')}
              options={districtsFor(city)}
              value={district || null}
              onChange={setDistrict}
            />
            {district === OTHER_DISTRICT && (
              <Input value={customDistrict} onChangeText={setCustomDistrict} placeholder={t('Име на квартала')} />
            )}
            <Muted>{t('По квартала известяваме търсещи наематели, когато имотът се освободи.')}</Muted>
          </>
        ) : city && findSettlement(city, place?.region)?.t === 'град' ? (
          <>
            <Label>{t('Квартал / район (по избор)')}</Label>
            <Input value={customDistrict} onChangeText={setCustomDistrict} placeholder={t('Име на квартала')} />
          </>
        ) : null}
        <Label>{t('Площ (кв.м)')}</Label>
        <Input value={area} onChangeText={setArea} keyboardType="decimal-pad" placeholder="68" />
        <Label>{t('Етаж')}</Label>
        <Input value={floor} onChangeText={setFloor} keyboardType="number-pad" placeholder="3" />
        <Label>{t('Стаи')}</Label>
        <Input value={rooms} onChangeText={setRooms} keyboardType="number-pad" placeholder="2" />
        <Label>{t('Описание')}</Label>
        <Input value={description} onChangeText={setDescription} multiline placeholder={t('Светъл двустаен…')} />

        <Label>{t('Снимки ({n})', { n: photos.length })}</Label>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {photos.map((p) => (
            <Image key={p} source={{ uri: `${API_URL}${p}` }} style={{ width: 72, height: 72, borderRadius: 8 }} />
          ))}
        </View>
        <Button title={t('+ Добави снимка')} variant="secondary" onPress={pickPhoto} />
        <Muted>{t('Снимките се качват веднага в хранилището.')}</Muted>

        {isAgency && (
          <>
            <Label>{t('Собственик — телефон (кани се по SMS)')}</Label>
            <Input value={ownerPhone} onChangeText={setOwnerPhone} keyboardType="phone-pad" placeholder="+359 88 700 0000" />
            <Label>{t('Собственик — име и фамилия')}</Label>
            <Input value={ownerFirstName} onChangeText={setOwnerFirstName} placeholder={t('Стоян')} />
            <Input value={ownerLastName} onChangeText={setOwnerLastName} placeholder={t('Стоянов')} style={{ marginTop: 8 }} />
            <Label>{t('Процент за управление (празно = по подразбиране)')}</Label>
            <Input value={managementPercent} onChangeText={setManagementPercent} keyboardType="decimal-pad" placeholder="10" />
          </>
        )}

        {billingPreview && <Muted>💳 {billingPreview}</Muted>}
        <Button
          title={t('Запази имота')}
          onPress={submit}
          disabled={!address || !city || (isAgency && !ownerPhone)}
          loading={loading}
        />
      </Card>
    </Screen>
  );
}
